// batch-67 hr-brieven (FO-506) — Types, labels, Zod-schema's en DTO's voor de
// HR-besluitworkflows: verlenging en omzetting van een contract, opzegging van
// een contract voor bepaalde tijd, non-actiefstelling, de vaststellings-
// overeenkomst (VSO), ontslag op staande voet en de acceptatie van een opzegging
// door de medewerker.
//
// Kern: elk besluit is een `HrBesluitWorkflow` met een vaste reeks
// `HrBesluitStap`-rijen. De PURE regels (statusmachine, stappenplan, termijnen,
// opzegtermijn, tag-controle) staan in `@streep/utils` (`hr-besluiten.ts`); de
// motor in `apps/api/src/services/hr-besluiten/`. De zeven brieven zijn
// beheerbare DOCX-sjablonen (`ClientDocumentTemplate`, scope MEDEWERKER) met een
// eigen categorie; een sjabloon dat een verplichte tag mist kan NIET gebruikt
// worden (de generator blokkeert, zie `HR_BRIEF_VERPLICHTE_TAGS`).
//
// Datums gaan over de API als 'yyyy-mm-dd' (NL-kalenderdag); tijdstippen als
// ISO-instant. In de DB staan dag-datums als UTC-middernacht van die kalenderdag.
// Geen salarisbedragen in audit of meldingen; het bedrag van een VSO-vergoeding
// staat uitsluitend in de workflow-gegevens (HR-dossier) en op de brief.

import { z } from 'zod'
import type { DocumentTemplateCategory } from './document-templates'

const ISO_DAG = /^\d{4}-\d{2}-\d{2}$/
const dagSchema = (label: string) =>
  z.string().min(1, `${label} is verplicht.`).regex(ISO_DAG, `Geef ${label.toLowerCase()} op als jjjj-mm-dd.`)

// ── Enums (mirror de Prisma-enums) ───────────────────────────────────────────

export const HR_BESLUIT_TYPES = [
  'VERLENGING_BEPAALDE_TIJD',
  'OMZETTING_ONBEPAALDE_TIJD',
  'OPZEGGING_BEPAALDE_TIJD',
  'NON_ACTIEFSTELLING',
  'VSO',
  'ONTSLAG_STAANDE_VOET',
  'ACCEPTATIE_OPZEGGING',
] as const
export type HrBesluitType = (typeof HR_BESLUIT_TYPES)[number]

export const HR_BESLUIT_TYPE_LABELS: Record<HrBesluitType, string> = {
  VERLENGING_BEPAALDE_TIJD: 'Verlenging bepaalde tijd',
  OMZETTING_ONBEPAALDE_TIJD: 'Omzetting naar onbepaalde tijd',
  OPZEGGING_BEPAALDE_TIJD: 'Opzegging bepaalde tijd (aanzegging)',
  NON_ACTIEFSTELLING: 'Non-actiefstelling',
  VSO: 'Vaststellingsovereenkomst',
  ONTSLAG_STAANDE_VOET: 'Ontslag op staande voet',
  ACCEPTATIE_OPZEGGING: 'Acceptatie opzegging medewerker',
}

/**
 * De sjablooncategorie van de hoofdbrief per besluittype. De zeven categorieën
 * zijn nieuw in `DocumentTemplateCategory` (losse ADD VALUE-migratie); de
 * opheffingsbrief van een non-actiefstelling gebruikt dezelfde categorie met
 * sjabloon-variant `OPHEFFING`.
 */
export const HR_BESLUIT_BRIEF_CATEGORIE: Record<HrBesluitType, DocumentTemplateCategory> = {
  VERLENGING_BEPAALDE_TIJD: 'VERLENGING_BEPAALDE_TIJD',
  OMZETTING_ONBEPAALDE_TIJD: 'OMZETTING_ONBEPAALDE_TIJD',
  OPZEGGING_BEPAALDE_TIJD: 'OPZEGGING_BEPAALDE_TIJD',
  NON_ACTIEFSTELLING: 'NON_ACTIEFSTELLING',
  VSO: 'VSO',
  ONTSLAG_STAANDE_VOET: 'ONTSLAG_STAANDE_VOET',
  ACCEPTATIE_OPZEGGING: 'ACCEPTATIE_OPZEGGING',
}

/** De zeven HR-briefcategorieën: de tag-scan blokkeert hier in plaats van te waarschuwen. */
export const HR_BRIEF_CATEGORIEEN: readonly DocumentTemplateCategory[] = [
  'VERLENGING_BEPAALDE_TIJD',
  'OMZETTING_ONBEPAALDE_TIJD',
  'OPZEGGING_BEPAALDE_TIJD',
  'NON_ACTIEFSTELLING',
  'VSO',
  'ONTSLAG_STAANDE_VOET',
  'ACCEPTATIE_OPZEGGING',
]

export function isHrBriefCategorie(category: string | null | undefined): category is HrBesluitType {
  return (HR_BRIEF_CATEGORIEEN as readonly string[]).includes(category ?? '')
}

/** Sjabloon-variant van de opheffingsbrief (non-actiefstelling opgeheven). */
export const HR_BRIEF_VARIANT_OPHEFFING = 'OPHEFFING'

/**
 * De verplichte samenvoegvelden per brief (spec §A), als canonieke tags. De
 * controle vergelijkt met `canonicalPlaceholderKey`, dus `{{Naam}}`,
 * `{{NIEUWE_STARTDATUM}}` en `{{nieuwe startdatum}}` tellen allemaal. `naam` en
 * `datum` komen uit de medewerker-autofill; de rest levert de workflow.
 */
export const HR_BRIEF_VERPLICHTE_TAGS: Record<HrBesluitType, readonly string[]> = {
  VERLENGING_BEPAALDE_TIJD: [
    'naam',
    'datum',
    'nieuwe_startdatum',
    'nieuwe_einddatum',
    'functie',
    'uren',
    'salaris',
    'ketennummer',
  ],
  OMZETTING_ONBEPAALDE_TIJD: ['naam', 'datum', 'ingangsdatum', 'functie', 'uren', 'salaris'],
  OPZEGGING_BEPAALDE_TIJD: [
    'naam',
    'datum',
    'einddatum',
    'aanzegdatum',
    'verlofsaldo_indicatie',
    'inname_afspraak',
  ],
  NON_ACTIEFSTELLING: [
    'naam',
    'datum',
    'ingangsdatum',
    'verwachte_duur',
    'variant_verlof',
    'loondoorbetaling',
    'bereikbaarheid',
    'inname_middelen',
  ],
  VSO: [
    'naam',
    'datum',
    'einddatum',
    'opzegtermijn_in_acht_genomen',
    'vergoeding',
    'eindafrekening',
    'vrijstelling_van_werk',
    'afspraken',
    'bedenktermijn',
    'inhoudelijke_punten',
  ],
  ONTSLAG_STAANDE_VOET: [
    'naam',
    'datum',
    'datum_tijd_dringende_reden',
    'feiten',
    'onverwijlde_mededeling',
    'einddatum',
    'inname',
    'eindafrekening',
  ],
  ACCEPTATIE_OPZEGGING: [
    'naam',
    'datum',
    'ontvangstdatum_opzegging',
    'opzegtermijn',
    'einddatum',
    'verlofkeuze',
    'inname',
    'eindafrekening',
  ],
}

/** Verplichte tags van de opheffingsbrief (variant OPHEFFING, categorie NON_ACTIEFSTELLING). */
export const HR_BRIEF_VERPLICHTE_TAGS_OPHEFFING: readonly string[] = ['naam', 'datum', 'opheffingsdatum']

export const HR_BESLUIT_WORKFLOW_STATUSSEN = [
  'CONCEPT',
  'TER_CONTROLE',
  'LOPEND',
  'AFGEROND',
  'HERROEPEN',
  'GEANNULEERD',
] as const
export type HrBesluitWorkflowStatus = (typeof HR_BESLUIT_WORKFLOW_STATUSSEN)[number]

export const HR_BESLUIT_WORKFLOW_STATUS_LABELS: Record<HrBesluitWorkflowStatus, string> = {
  CONCEPT: 'Concept',
  TER_CONTROLE: 'Ter controle (4-ogen)',
  LOPEND: 'Lopend',
  AFGEROND: 'Afgerond',
  HERROEPEN: 'Herroepen',
  GEANNULEERD: 'Geannuleerd',
}

/** Statussen waarin de workflow nog werk vraagt (de werkvoorraad van het overzicht). */
export const HR_BESLUIT_OPEN_STATUSSEN = ['CONCEPT', 'TER_CONTROLE', 'LOPEND'] as const satisfies readonly HrBesluitWorkflowStatus[]

export const HR_BESLUIT_STAP_TYPES = [
  'KETENCHECK',
  'AANZEGCHECK',
  'VERZUIMCHECK',
  'FEITEN_VASTGELEGD',
  'OPZEGGING_GEREGISTREERD',
  'OPZEGTERMIJN_BEREKEND',
  'SCHRIFTELIJKE_BEVESTIGING',
  'BRIEF_CONCEPT',
  'CONTROLE_4_OGEN',
  'BRIEF_DEFINITIEF',
  'BRIEF_VERSTREKKEN',
  'VERZENDING_ONVERWIJLD',
  'CONTRACT_AANMAKEN',
  'CONTRACT_ONDERTEKENEN',
  'CONTRACT_ACTIVEREN',
  'CONTRACTMUTATIE',
  'SALARISVERWERKER_BERICHT',
  'WERKSTATUS_NON_ACTIEF',
  'VERLOF_AFBOEKEN',
  'HERBEOORDELING',
  'OPHEFFING',
  'ONDERTEKENING',
  'BEDENKTERMIJN',
  'VRIJSTELLING_WERK',
  'TOEGANG_BLOKKEREN',
  'PLANNING_LEEGMAKEN',
  'MIDDELEN_INNEMEN',
  'OFFBOARDING_STARTEN',
  'GETUIGSCHRIFT',
  'EINDAFREKENING',
  'BEZWAARTERMIJN',
  'VERLOFKEUZE',
] as const
export type HrBesluitStapType = (typeof HR_BESLUIT_STAP_TYPES)[number]

export const HR_BESLUIT_STAP_LABELS: Record<HrBesluitStapType, string> = {
  KETENCHECK: 'Ketenregeling gecontroleerd',
  AANZEGCHECK: 'Aanzegtermijn gecontroleerd',
  VERZUIMCHECK: 'Verzuim gecontroleerd',
  FEITEN_VASTGELEGD: 'Feiten en dringende reden vastgelegd',
  OPZEGGING_GEREGISTREERD: 'Opzegging geregistreerd',
  OPZEGTERMIJN_BEREKEND: 'Opzegtermijn berekend',
  SCHRIFTELIJKE_BEVESTIGING: 'Schriftelijke bevestiging van de opzegging vragen',
  BRIEF_CONCEPT: 'Brief opstellen (concept)',
  CONTROLE_4_OGEN: 'Controle door een tweede paar ogen',
  BRIEF_DEFINITIEF: 'Brief definitief maken',
  BRIEF_VERSTREKKEN: 'Brief verstrekken of laten ondertekenen',
  VERZENDING_ONVERWIJLD: 'Onverwijld verzenden (aangetekend, mail, portal)',
  CONTRACT_AANMAKEN: 'Nieuw contract aanmaken en ter ondertekening aanbieden',
  CONTRACT_ONDERTEKENEN: 'Contract ondertekend door de medewerker',
  CONTRACT_ACTIVEREN: 'Contract activeren op de ingangsdatum',
  CONTRACTMUTATIE: 'Contractmutatie vastleggen (uren, salaris)',
  SALARISVERWERKER_BERICHT: 'Bericht aan de salarisverwerker',
  WERKSTATUS_NON_ACTIEF: 'Werkstatus op non-actief',
  VERLOF_AFBOEKEN: 'Verlof afboeken volgens de gekozen variant',
  HERBEOORDELING: 'Herbeoordeling op de einddatum',
  OPHEFFING: 'Non-actiefstelling opheffen (brief en werkstatus)',
  ONDERTEKENING: 'Ondertekening door de medewerker',
  BEDENKTERMIJN: 'Bedenktermijn',
  VRIJSTELLING_WERK: 'Vrijstelling van werk (planning geblokkeerd)',
  TOEGANG_BLOKKEREN: 'Toegang blokkeren',
  PLANNING_LEEGMAKEN: 'Planning leegmaken',
  MIDDELEN_INNEMEN: 'Middelen innemen',
  OFFBOARDING_STARTEN: 'Offboarding starten',
  GETUIGSCHRIFT: 'Getuigschrift',
  EINDAFREKENING: 'Eindafrekening',
  BEZWAARTERMIJN: 'Bezwaartermijn van de medewerker (2 maanden)',
  VERLOFKEUZE: 'Verlofkeuze: opnemen of uitbetalen',
}

export const HR_BESLUIT_STAP_STATUSSEN = ['WACHT', 'OPEN', 'AFGEROND', 'OVERGESLAGEN', 'VERVALLEN'] as const
export type HrBesluitStapStatus = (typeof HR_BESLUIT_STAP_STATUSSEN)[number]

export const HR_BESLUIT_STAP_STATUS_LABELS: Record<HrBesluitStapStatus, string> = {
  WACHT: 'Wacht op een eerdere stap',
  OPEN: 'Open',
  AFGEROND: 'Afgerond',
  OVERGESLAGEN: 'Overgeslagen',
  VERVALLEN: 'Vervallen',
}

/** Stapstatussen die als "klaar" tellen voor de blokkade van vervolgstappen. */
export const HR_BESLUIT_STAP_KLAAR_STATUSSEN = ['AFGEROND', 'OVERGESLAGEN'] as const satisfies readonly HrBesluitStapStatus[]

export const HR_BESLUIT_ROLLEN = ['HR', 'DIRECTIE', 'SALARIS', 'TEAMLEIDER', 'FACILITIES', 'PLANNER'] as const
export type HrBesluitRol = (typeof HR_BESLUIT_ROLLEN)[number]

export const HR_BESLUIT_ROL_LABELS: Record<HrBesluitRol, string> = {
  HR: 'HR',
  DIRECTIE: 'Directie',
  SALARIS: 'Salarisadministratie',
  TEAMLEIDER: 'Teamleider',
  FACILITIES: 'Wagenpark en magazijn',
  PLANNER: 'Planning',
}

export const HR_BESLUIT_BRIEF_SOORTEN = ['HOOFD', 'OPHEFFING'] as const
export type HrBesluitBriefSoort = (typeof HR_BESLUIT_BRIEF_SOORTEN)[number]

export const HR_BESLUIT_BRIEF_STATUSSEN = [
  'CONCEPT',
  'TER_CONTROLE',
  'DEFINITIEF',
  'VERSTREKT',
  'ONDERTEKEND',
  'PER_POST',
] as const
export type HrBesluitBriefStatus = (typeof HR_BESLUIT_BRIEF_STATUSSEN)[number]

export const HR_BESLUIT_BRIEF_STATUS_LABELS: Record<HrBesluitBriefStatus, string> = {
  CONCEPT: 'Concept',
  TER_CONTROLE: 'Ter controle',
  DEFINITIEF: 'Definitief',
  VERSTREKT: 'Verstrekt in de portal',
  ONDERTEKEND: 'Ondertekend',
  PER_POST: 'Per post verzonden',
}

export const HR_BESLUIT_CONTROLE_UITKOMSTEN = ['GOEDGEKEURD', 'AFGEWEZEN'] as const
export type HrBesluitControleUitkomst = (typeof HR_BESLUIT_CONTROLE_UITKOMSTEN)[number]

export const NON_ACTIEF_VARIANTEN = ['MET_BEHOUD_VERLOF', 'ZONDER_BEHOUD_VERLOF'] as const
export type NonActiefVariant = (typeof NON_ACTIEF_VARIANTEN)[number]

export const NON_ACTIEF_VARIANT_LABELS: Record<NonActiefVariant, string> = {
  MET_BEHOUD_VERLOF: 'Met behoud van verlof',
  ZONDER_BEHOUD_VERLOF: 'Zonder behoud van verlof (verlof wordt afgeboekt)',
}

export const OPZEGGING_BRONNEN = ['BRIEF', 'EMAIL', 'WHATSAPP', 'MONDELING'] as const
export type OpzeggingBron = (typeof OPZEGGING_BRONNEN)[number]

export const OPZEGGING_BRON_LABELS: Record<OpzeggingBron, string> = {
  BRIEF: 'Brief',
  EMAIL: 'E-mail',
  WHATSAPP: 'WhatsApp of bericht',
  MONDELING: 'Mondeling',
}

/** Bronnen die geen schriftelijke opzegging zijn: er volgt een taak om die te vragen (§5.5). */
export const OPZEGGING_BRONNEN_ZONDER_SCHRIFT: readonly OpzeggingBron[] = ['WHATSAPP', 'MONDELING']

export const VERLOF_KEUZES = ['OPNEMEN', 'UITBETALEN'] as const
export type VerlofKeuze = (typeof VERLOF_KEUZES)[number]

export const VERLOF_KEUZE_LABELS: Record<VerlofKeuze, string> = {
  OPNEMEN: 'Opnemen vóór de einddatum',
  UITBETALEN: 'Uitbetalen bij de eindafrekening',
}

export const HERBEOORDELING_UITKOMSTEN = ['VERLENGEN', 'OPHEFFEN', 'VERVOLGBESLUIT'] as const
export type HerbeoordelingUitkomst = (typeof HERBEOORDELING_UITKOMSTEN)[number]

export const HERBEOORDELING_UITKOMST_LABELS: Record<HerbeoordelingUitkomst, string> = {
  VERLENGEN: 'Verlengen (nieuwe einddatum)',
  OPHEFFEN: 'Opheffen (medewerker weer actief)',
  VERVOLGBESLUIT: 'Vervolgbesluit (ander traject)',
}

export const VERZENDKANALEN = ['AANGETEKEND', 'EMAIL', 'PORTAL', 'PERSOONLIJK'] as const
export type Verzendkanaal = (typeof VERZENDKANALEN)[number]

export const VERZENDKANAAL_LABELS: Record<Verzendkanaal, string> = {
  AANGETEKEND: 'Aangetekende brief',
  EMAIL: 'E-mail',
  PORTAL: 'Portal',
  PERSOONLIJK: 'Persoonlijk overhandigd',
}

export const OPZEGTERMIJN_BRONNEN = ['CAO', 'WETTELIJK'] as const
export type OpzegtermijnBron = (typeof OPZEGTERMIJN_BRONNEN)[number]

export const OPZEGTERMIJN_BRON_LABELS: Record<OpzegtermijnBron, string> = {
  CAO: 'CAO Metaal en Techniek',
  WETTELIJK: 'Wettelijk (BW 7:672)',
}

export const OPZEGTERMIJN_PARTIJEN = ['WERKNEMER', 'WERKGEVER'] as const
export type OpzegtermijnPartij = (typeof OPZEGTERMIJN_PARTIJEN)[number]

export const OPZEGTERMIJN_PARTIJ_LABELS: Record<OpzegtermijnPartij, string> = {
  WERKNEMER: 'Opzegging door de medewerker',
  WERKGEVER: 'Opzegging door de werkgever',
}

// ── Termijnen (vaste wettelijke waarden, A2) ─────────────────────────────────

/** Bedenktermijn VSO: 14 dagen, 21 dagen als de VSO de termijn niet vermeldt (art. 7:670b BW). */
export const VSO_BEDENKTERMIJN_DAGEN = 14
export const VSO_BEDENKTERMIJN_DAGEN_ZONDER_VERMELDING = 21
/** Bezwaartermijn van de medewerker na ontslag op staande voet (art. 7:686a BW): 2 maanden. */
export const STAANDE_VOET_BEZWAARTERMIJN_MAANDEN = 2
/** Aanzegtermijn: uiterlijk een maand voor het einde van het contract (art. 7:668 BW). */
export const AANZEGTERMIJN_MAANDEN = 1
/** Herinnering: zoveel dagen voor een termijn stuurt de bewaking een melding. */
export const HR_BESLUIT_TERMIJN_HERINNERING_DAGEN = 7

// ── Zod: starten per type ───────────────────────────────────────────────────

/** Eén inhoudelijk punt van een VSO (kop plus tekst), vrij of uit de bibliotheek. */
export const vsoPuntSchema = z.object({
  kop: z.string().trim().min(1, 'Geef een kop op.').max(120),
  tekst: z.string().trim().min(1, 'Geef de tekst van het punt op.').max(4000),
})
export type VsoPuntInput = z.infer<typeof vsoPuntSchema>

const basis = {
  employeeId: z.string().min(1, 'Kies een medewerker.'),
  toelichting: z.string().trim().max(2000).optional(),
}

const urenPerWeekSchema = z
  .number({ message: 'Geef de uren per week op als getal.' })
  .positive('De uren per week moeten meer dan nul zijn.')
  .max(80, 'Meer dan 80 uur per week kan niet.')
const bedragSchema = z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geef een bedrag op met maximaal twee decimalen.')

export const hrBesluitVerlengingSchema = z
  .object({
    ...basis,
    type: z.literal('VERLENGING_BEPAALDE_TIJD'),
    employmentContractId: z.string().min(1, 'Kies het aflopende contract.'),
    nieuweStartDatum: dagSchema('Nieuwe startdatum'),
    nieuweEindDatum: dagSchema('Nieuwe einddatum'),
    functie: z.string().trim().max(160).optional(),
    urenPerWeek: urenPerWeekSchema.optional(),
    /** Nieuw bruto maandsalaris als decimaal-string ("3250.00"); leeg = het huidige salaris uit het dossier. */
    salaris: bedragSchema.optional(),
    voorwaardenGewijzigd: z.boolean().default(false),
    ketenOverride: z.boolean().optional(),
    ketenOverrideMotivatie: z.string().trim().max(1000).optional(),
    mijlpaalId: z.string().min(1).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.nieuweEindDatum <= v.nieuweStartDatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nieuweEindDatum'],
        message: 'De nieuwe einddatum moet na de nieuwe startdatum liggen.',
      })
    }
    if (v.ketenOverride && !v.ketenOverrideMotivatie?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ketenOverrideMotivatie'],
        message: 'Een override op de ketenregeling vraagt een motivatie.',
      })
    }
  })

export const hrBesluitOmzettingSchema = z.object({
  ...basis,
  type: z.literal('OMZETTING_ONBEPAALDE_TIJD'),
  employmentContractId: z.string().min(1, 'Kies het aflopende contract.'),
  ingangsdatum: dagSchema('Ingangsdatum'),
  functie: z.string().trim().max(160).optional(),
  urenPerWeek: urenPerWeekSchema.optional(),
  /** Nieuw bruto maandsalaris als decimaal-string; leeg = het huidige salaris uit het dossier. */
  salaris: bedragSchema.optional(),
  voorwaardenGewijzigd: z.boolean().default(false),
  mijlpaalId: z.string().min(1).optional(),
})

export const hrBesluitOpzeggingSchema = z.object({
  ...basis,
  type: z.literal('OPZEGGING_BEPAALDE_TIJD'),
  employmentContractId: z.string().min(1, 'Kies het aflopende contract.'),
  reden: z.string().trim().max(2000).optional(),
  innameAfspraak: z.string().trim().max(500).optional(),
  mijlpaalId: z.string().min(1).optional(),
})

export const hrBesluitNonActiefSchema = z
  .object({
    ...basis,
    type: z.literal('NON_ACTIEFSTELLING'),
    ingangsdatum: dagSchema('Ingangsdatum'),
    einddatum: z.string().regex(ISO_DAG, 'Geef de einddatum op als jjjj-mm-dd.').nullable().optional(),
    variant: z.enum(NON_ACTIEF_VARIANTEN, { message: 'Kies de variant.' }),
    reden: z.string().trim().max(2000).optional(),
    loondoorbetaling: z.boolean().default(true),
    bereikbaarheid: z.string().trim().max(500).optional(),
    innameMiddelen: z.boolean().default(false),
    portalToegangBeperken: z.boolean().default(false),
    /** §5.2: loopt er verzuim, dan alleen met deze expliciete bevestiging. */
    bevestigOndanksVerzuim: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.variant === 'ZONDER_BEHOUD_VERLOF' && !v.einddatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['einddatum'],
        message: 'Zonder behoud van verlof is een einddatum verplicht: het af te boeken verlof wordt over die periode berekend.',
      })
    }
    if (v.einddatum && v.einddatum < v.ingangsdatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['einddatum'],
        message: 'De einddatum mag niet voor de ingangsdatum liggen.',
      })
    }
  })

export const hrBesluitVsoSchema = z.object({
  ...basis,
  type: z.literal('VSO'),
  einddatum: dagSchema('Einddatum'),
  opzegtermijnInAchtGenomen: z.boolean().default(true),
  /** Beëindigingsvergoeding als decimaal-string ("2500.00"); leeg = geen vergoeding. */
  vergoeding: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geef een bedrag op met maximaal twee decimalen.').optional(),
  vrijstellingVanWerk: z.boolean().default(false),
  vrijstellingVanaf: z.string().regex(ISO_DAG).optional(),
  concurrentiebeding: z.string().trim().max(2000).optional(),
  geheimhouding: z.string().trim().max(2000).optional(),
  /** Vermeldt de VSO de bedenktermijn? Zo niet, dan geldt 21 dagen (art. 7:670b BW). */
  bedenktermijnVermeld: z.boolean().default(true),
  punten: z.array(vsoPuntSchema).max(50).default([]),
})

export const hrBesluitStaandeVoetSchema = z.object({
  ...basis,
  type: z.literal('ONTSLAG_STAANDE_VOET'),
  /** Het moment van de dringende reden en van de onverwijlde mededeling (ISO-instant). */
  moment: z.string().datetime({ offset: true, message: 'Geef datum en tijd van de dringende reden op.' }),
  dringendeReden: z.string().trim().min(5, 'Omschrijf de dringende reden.').max(500),
  feiten: z.string().trim().min(20, 'Leg de feiten vast (minimaal 20 tekens).').max(8000),
  innameSpoed: z.boolean().default(true),
})

export const hrBesluitAcceptatieSchema = z
  .object({
    ...basis,
    type: z.literal('ACCEPTATIE_OPZEGGING'),
    employmentContractId: z.string().min(1).optional(),
    ontvangenOp: dagSchema('Ontvangstdatum van de opzegging'),
    bron: z.enum(OPZEGGING_BRONNEN, { message: 'Kies hoe de opzegging is ontvangen.' }),
    gewensteEinddatum: z.string().regex(ISO_DAG, 'Geef de gewenste einddatum op als jjjj-mm-dd.').nullable().optional(),
    verlofKeuze: z.enum(VERLOF_KEUZES).nullable().optional(),
    /** Het document of de mail van de medewerker in het dossier. */
    opzeggingDocumentId: z.string().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.gewensteEinddatum && v.gewensteEinddatum < v.ontvangenOp) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gewensteEinddatum'],
        message: 'De gewenste einddatum ligt voor de ontvangstdatum.',
      })
    }
  })

export const hrBesluitStartSchema = z.discriminatedUnion('type', [
  hrBesluitVerlengingSchema,
  hrBesluitOmzettingSchema,
  hrBesluitOpzeggingSchema,
  hrBesluitNonActiefSchema,
  hrBesluitVsoSchema,
  hrBesluitStaandeVoetSchema,
  hrBesluitAcceptatieSchema,
])
export type HrBesluitStartInput = z.infer<typeof hrBesluitStartSchema>
export type HrBesluitVerlengingInput = z.infer<typeof hrBesluitVerlengingSchema>
export type HrBesluitOmzettingInput = z.infer<typeof hrBesluitOmzettingSchema>
export type HrBesluitOpzeggingInput = z.infer<typeof hrBesluitOpzeggingSchema>
export type HrBesluitNonActiefInput = z.infer<typeof hrBesluitNonActiefSchema>
export type HrBesluitVsoInput = z.infer<typeof hrBesluitVsoSchema>
export type HrBesluitStaandeVoetInput = z.infer<typeof hrBesluitStaandeVoetSchema>
export type HrBesluitAcceptatieInput = z.infer<typeof hrBesluitAcceptatieSchema>

/** Labels voor inline veldfouten (issuesToFieldErrors/summarizeValidationIssues). */
export const HR_BESLUIT_VELD_LABELS: Record<string, string> = {
  employeeId: 'Medewerker',
  employmentContractId: 'Contract',
  nieuweStartDatum: 'Nieuwe startdatum',
  nieuweEindDatum: 'Nieuwe einddatum',
  ingangsdatum: 'Ingangsdatum',
  einddatum: 'Einddatum',
  functie: 'Functie',
  urenPerWeek: 'Uren per week',
  salaris: 'Salaris',
  kanaal: 'Kanaal',
  opzeggingDocumentId: 'Document van de opzegging',
  vrijstellingVanaf: 'Vrijstelling vanaf',
  variant: 'Variant',
  reden: 'Reden',
  moment: 'Datum en tijd',
  dringendeReden: 'Dringende reden',
  feiten: 'Feiten',
  ontvangenOp: 'Ontvangstdatum',
  bron: 'Bron',
  gewensteEinddatum: 'Gewenste einddatum',
  vergoeding: 'Vergoeding',
  punten: 'Inhoudelijke punten',
  ketenOverrideMotivatie: 'Motivatie override',
  toelichting: 'Toelichting',
  uitkomst: 'Uitkomst',
  verzondenOp: 'Verzonden op',
  kanalen: 'Kanalen',
  herroepenOp: 'Herroepen op',
  ondertekendOp: 'Ondertekend op',
  nieuweEinddatum: 'Nieuwe einddatum',
  termijnMaanden: 'Termijn (maanden)',
  minDienstjaren: 'Dienstjaren vanaf',
  maxDienstjaren: 'Dienstjaren tot',
  kop: 'Kop',
  tekst: 'Tekst',
  verlofKeuze: 'Verlofkeuze',
}

// ── Zod: acties op een workflow ─────────────────────────────────────────────

export const hrBesluitControleSchema = z.object({
  uitkomst: z.enum(HR_BESLUIT_CONTROLE_UITKOMSTEN),
  toelichting: z.string().trim().max(2000).optional(),
})
export type HrBesluitControleInput = z.infer<typeof hrBesluitControleSchema>

export const hrBesluitStapAfrondenSchema = z.object({
  toelichting: z.string().trim().max(2000).optional(),
})
export type HrBesluitStapAfrondenInput = z.infer<typeof hrBesluitStapAfrondenSchema>

export const hrBesluitStapOverslaanSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden op.').max(2000),
})
export type HrBesluitStapOverslaanInput = z.infer<typeof hrBesluitStapOverslaanSchema>

export const hrBesluitAnnulerenSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden op.').max(2000),
})
export type HrBesluitAnnulerenInput = z.infer<typeof hrBesluitAnnulerenSchema>

export const hrBesluitBriefGenererenSchema = z.object({
  soort: z.enum(HR_BESLUIT_BRIEF_SOORTEN).default('HOOFD'),
  /** Extra of overschreven samenvoegwaarden (vrije tekstvelden van de brief). */
  placeholderValues: z.record(z.string(), z.string()).optional(),
  briefpapier: z
    .object({ entityId: z.string().min(1).nullable().optional(), variant: z.string().min(1).nullable().optional() })
    .nullable()
    .optional(),
})
export type HrBesluitBriefGenererenInput = z.infer<typeof hrBesluitBriefGenererenSchema>

export const hrBesluitBriefVerstrekkenSchema = z.object({
  soort: z.enum(HR_BESLUIT_BRIEF_SOORTEN).default('HOOFD'),
  kanaal: z.enum(['PORTAL', 'ONDERTEKENEN', 'POST']),
  /** Alleen bij POST: de dag van verzending. */
  verzondenOp: z.string().regex(ISO_DAG).optional(),
})
export type HrBesluitBriefVerstrekkenInput = z.infer<typeof hrBesluitBriefVerstrekkenSchema>

export const hrBesluitVerzendingSchema = z.object({
  verzondenOp: z.string().datetime({ offset: true, message: 'Geef het tijdstip van verzending op.' }),
  kanalen: z.array(z.enum(VERZENDKANALEN)).min(1, 'Kies minimaal één kanaal.'),
  toelichting: z.string().trim().max(2000).optional(),
})
export type HrBesluitVerzendingInput = z.infer<typeof hrBesluitVerzendingSchema>

export const hrBesluitOndertekeningPerPostSchema = z.object({
  ondertekendOp: dagSchema('Datum van ondertekening'),
})
export type HrBesluitOndertekeningPerPostInput = z.infer<typeof hrBesluitOndertekeningPerPostSchema>

export const hrBesluitHerroepingSchema = z.object({
  herroepenOp: dagSchema('Datum van herroeping'),
  bron: z.enum(OPZEGGING_BRONNEN).default('BRIEF'),
  toelichting: z.string().trim().max(2000).optional(),
})
export type HrBesluitHerroepingInput = z.infer<typeof hrBesluitHerroepingSchema>

export const hrBesluitHerbeoordelingSchema = z
  .object({
    uitkomst: z.enum(HERBEOORDELING_UITKOMSTEN),
    nieuweEinddatum: z.string().regex(ISO_DAG, 'Geef de nieuwe einddatum op als jjjj-mm-dd.').nullable().optional(),
    toelichting: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.uitkomst === 'VERLENGEN' && !v.nieuweEinddatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nieuweEinddatum'],
        message: 'Geef bij verlengen de nieuwe einddatum op.',
      })
    }
  })
export type HrBesluitHerbeoordelingInput = z.infer<typeof hrBesluitHerbeoordelingSchema>

export const hrBesluitVerlofKeuzeSchema = z.object({
  verlofKeuze: z.enum(VERLOF_KEUZES, { message: 'Kies opnemen of uitbetalen.' }),
})
export type HrBesluitVerlofKeuzeInput = z.infer<typeof hrBesluitVerlofKeuzeSchema>

// ── Zod: instellingen ───────────────────────────────────────────────────────

export const opzegtermijnRegelSchema = z
  .object({
    bron: z.enum(OPZEGTERMIJN_BRONNEN),
    partij: z.enum(OPZEGTERMIJN_PARTIJEN),
    minDienstjaren: z.number().int().min(0).max(60),
    maxDienstjaren: z.number().int().min(1).max(60).nullable(),
    termijnMaanden: z.number().int().min(0).max(12),
    omschrijving: z.string().trim().max(200).optional(),
    actief: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.maxDienstjaren !== null && v.maxDienstjaren <= v.minDienstjaren) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxDienstjaren'],
        message: 'De bovengrens moet groter zijn dan de ondergrens.',
      })
    }
  })
export type OpzegtermijnRegelInput = z.infer<typeof opzegtermijnRegelSchema>

export const vsoBibliotheekPuntSchema = vsoPuntSchema.extend({
  volgorde: z.number().int().min(0).max(1000).default(0),
  actief: z.boolean().default(true),
})
export type VsoBibliotheekPuntInput = z.infer<typeof vsoBibliotheekPuntSchema>

export const opzegtermijnBerekenSchema = z.object({
  employeeId: z.string().min(1),
  partij: z.enum(OPZEGTERMIJN_PARTIJEN).default('WERKNEMER'),
  ontvangenOp: dagSchema('Ontvangstdatum'),
  gewensteEinddatum: z.string().regex(ISO_DAG).nullable().optional(),
})
export type OpzegtermijnBerekenInput = z.infer<typeof opzegtermijnBerekenSchema>

// ── Zod: lijst-query ────────────────────────────────────────────────────────

export const HR_BESLUIT_TERMIJN_FILTERS = ['VERLOPEN', 'BINNEN_7_DAGEN'] as const
export type HrBesluitTermijnFilter = (typeof HR_BESLUIT_TERMIJN_FILTERS)[number]

/**
 * Het statusfilter van het overzicht: een echte status, of een van de twee
 * verzamelingen. Zonder filter toont de lijst de werkvoorraad (concept, ter
 * controle, lopend).
 *   ALLE     elke status (de keten van besluiten in het dossier);
 *   GESTOPT  herroepen of geannuleerd.
 */
export const HR_BESLUIT_LIJST_STATUS_FILTERS = [...HR_BESLUIT_WORKFLOW_STATUSSEN, 'ALLE', 'GESTOPT'] as const
export type HrBesluitLijstStatusFilter = (typeof HR_BESLUIT_LIJST_STATUS_FILTERS)[number]

export const hrBesluitLijstQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  type: z.enum(HR_BESLUIT_TYPES).optional(),
  status: z.enum(HR_BESLUIT_LIJST_STATUS_FILTERS).optional(),
  employeeId: z.string().min(1).optional(),
  entityId: z.string().min(1).optional(),
  termijn: z.enum(HR_BESLUIT_TERMIJN_FILTERS).optional(),
  zoek: z.string().trim().max(100).optional(),
})
export type HrBesluitLijstQuery = z.infer<typeof hrBesluitLijstQuerySchema>

// ── DTO's ───────────────────────────────────────────────────────────────────

export interface HrBesluitStapDTO {
  id: string
  type: HrBesluitStapType
  titel: string
  volgorde: number
  status: HrBesluitStapStatus
  verantwoordelijkeRol: HrBesluitRol
  /** Welke stappen eerst klaar moeten zijn (labels voor de uitleg bij WACHT). */
  vereisteStappen: HrBesluitStapType[]
  /** 'yyyy-mm-dd' of null. */
  deadlineOp: string | null
  /** True als de deadline verstreken is en de stap nog niet klaar is. */
  teLaat: boolean
  documentId: string | null
  taakId: string | null
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  overgeslagenReden: string | null
  toelichting: string | null
  /** Vrije uitkomst van automatische stappen (bv. afgeboekte uren, ketenstand). */
  uitkomst: Record<string, string | number | boolean | null> | null
  /** Welke handeling de UI aanbiedt voor deze stap. */
  actie: HrBesluitStapActie
  /** Mag de kijker die handeling nu doen (de rol van de stap, of HR)? De server beslist; het scherm toont de knop alleen dan. */
  magUitvoeren: boolean
  /** Mag de kijker deze stap overslaan (HR, alleen een open stap die over te slaan is)? */
  magOverslaan: boolean
  /** De stap volgt de offboarding-cockpit (inleverlijst, getuigschrift, eindafrekening) en heeft daar zijn taken. */
  volgtOffboarding: boolean
  /** Een automatische stap die is mislukt en op HR wacht (opnieuw proberen of handmatig afronden). */
  automatischMislukt: boolean
}

/** Wat de UI bij een stap kan doen. */
export type HrBesluitStapActie =
  | 'GEEN'
  | 'BRIEF_GENEREREN'
  | 'CONTROLE_AANVRAGEN'
  | 'CONTROLE'
  | 'BRIEF_DEFINITIEF'
  | 'BRIEF_VERSTREKKEN'
  | 'VERZENDING'
  | 'CONTRACT_AANMAKEN'
  | 'CONTRACT_ACTIVEREN'
  | 'HERBEOORDELING'
  | 'OPHEFFING'
  | 'ONDERTEKENING_PER_POST'
  | 'VERLOFKEUZE'
  | 'SALARISBERICHT'
  | 'AFRONDEN'
  | 'OPNIEUW_PROBEREN'
  /** De stap volgt de offboarding-cockpit: haal de stand daar op (sluit alleen als de cockpit hem klaar meldt). */
  | 'BIJWERKEN'

export interface HrBesluitBriefDTO {
  id: string
  soort: HrBesluitBriefSoort
  category: DocumentTemplateCategory
  status: HrBesluitBriefStatus
  documentId: string | null
  /** Is er een PDF om te downloaden (via het besluit zelf, niet via de documentenmodule)? */
  heeftPdf: boolean
  /**
   * De brief is buiten het besluit om gewijzigd (een nieuwe versie of een
   * vervangen bestand): hij kan zo niet verder en moet opnieuw gegenereerd.
   */
  gewijzigdBuitenBesluit: boolean
  gegenereerdOp: string | null
  gecontroleerdOp: string | null
  gecontroleerdDoorNaam: string | null
  definitiefOp: string | null
  verstrektOp: string | null
  ondertekendOp: string | null
  perPostOp: string | null
  /** Is het document nog een concept (niet definitief)? Dan kan de brief opnieuw gegenereerd worden. */
  herGenereerbaar: boolean
}

export interface HrBesluitTijdlijnItemDTO {
  id: string
  /** ISO-instant. */
  moment: string
  actie: string
  titel: string
  toelichting: string | null
  actorNaam: string | null
}

export interface HrBesluitTermijnDTO {
  soort: 'AANZEGTERMIJN' | 'BEDENKTERMIJN' | 'HERBEOORDELING' | 'BEZWAARTERMIJN' | 'OPZEGTERMIJN' | 'EINDDATUM'
  label: string
  /** 'yyyy-mm-dd'. */
  datum: string
  dagenResterend: number
  verstreken: boolean
  toelichting: string | null
}

export interface HrBesluitWorkflowDTO {
  id: string
  type: HrBesluitType
  typeLabel: string
  status: HrBesluitWorkflowStatus
  statusLabel: string
  employeeId: string
  medewerkerNaam: string
  entiteitNaam: string | null
  entityId: string | null
  employmentContractId: string | null
  nieuwContractId: string | null
  offboardingWorkflowId: string | null
  mijlpaalId: string | null
  gestartOp: string
  gestartDoorNaam: string | null
  afgerondOp: string | null
  geannuleerdOp: string | null
  geannuleerdReden: string | null
  herroepenOp: string | null
  /** Besluit-specifieke gegevens zoals ingevoerd (zonder audit-gevoelige bedragen in meldingen). */
  gegevens: Record<string, unknown>
  einddatum: string | null
  controleRol: HrBesluitRol
  controleDoorNaam: string | null
  controleOp: string | null
  controleUitkomst: HrBesluitControleUitkomst | null
  controleToelichting: string | null
  /** Wie de workflow startte mag zelf niet controleren (4-ogen). */
  gestartDoorId: string
  aanzegUiterlijkOp: string | null
  aangezegdOp: string | null
  teLaatAangezegd: boolean
  vergoedingsplichtGeregistreerd: boolean
  bedenktermijnDagen: number | null
  bedenktermijnTot: string | null
  bezwaartermijnTot: string | null
  herbeoordelingOp: string | null
  nonActiefVariant: NonActiefVariant | null
  verlofAfgeboektUren: string | null
  verlofKeuze: VerlofKeuze | null
  dossierVergrendeld: boolean
  /** Verzuim liep op het moment van starten (non-actief alleen met bevestiging). */
  verzuimBijStart: boolean
  /** Verzending ontslag op staande voet: tijdstip en kanalen. */
  verzondenOp: string | null
  verzendkanalen: Verzendkanaal[]
  termijnen: HrBesluitTermijnDTO[]
  stappen: HrBesluitStapDTO[]
  brieven: HrBesluitBriefDTO[]
  tijdlijn: HrBesluitTijdlijnItemDTO[]
  /** Wat de kijker mag: beheren (HR/ADMIN met schrijfrecht) en/of controleren (4-ogen). */
  magBeheren: boolean
  magControleren: boolean
  /** Het controleverzoek intrekken (terug naar concept): HR en beheer, zolang het ter controle staat. */
  magControleIntrekken: boolean
  /**
   * Staat het besluit ter controle: is er op dit moment iemand die het mag
   * controleren? `false` = het verzoek ligt bij niemand (intrekken, of een
   * beheerder vragen). Buiten de controle altijd `true`.
   */
  controleurBeschikbaar: boolean
  magAnnuleren: boolean
  /** Een herroeping van de VSO registreren (zolang het besluit loopt en de bedenktermijn is gestart). */
  magHerroepen: boolean
  /**
   * De opheffingsbrief staat definitief klaar maar is nog niet bij de medewerker
   * (geen portaltoegang op het moment van opheffen): HR verstrekt hem alsnog,
   * per post of in de portal. Kan ook nadat het besluit is afgerond.
   */
  magVerstrekkenOpheffingsbrief: boolean
  /**
   * Herroepen, maar het terugdraaien van de uitdienst is niet afgemaakt: de
   * herroeping is vastgelegd, het terugdraaien (einddatum, offboarding, toegang)
   * strandde. Het scherm zegt dat, in plaats van "teruggedraaid".
   */
  terugdraaiOnvolledig: boolean
  /** "Terugdraaien afmaken" mag: HR en beheer met schrijfrecht op medewerkers. */
  magTerugdraaiAfmaken: boolean
  /** Het zicht van de kijker: alles, of alleen als salarisadministratie. */
  zicht: 'VOLLEDIG' | 'SALARIS'
  /** Wat HR eerder in het scherm heeft aangevuld voor de brief (tag -> waarde). */
  briefAanvullingen: Record<string, string>
}

/** Waar de link van een melding "stap open" de kijker heen brengt. */
export type HrBesluitStapDoelDTO =
  | { soort: 'BESLUIT'; workflowId: string }
  | { soort: 'TAAK'; taakId: string }
  | { soort: 'GEEN' }

export interface HrBesluitLijstRijDTO {
  id: string
  type: HrBesluitType
  typeLabel: string
  status: HrBesluitWorkflowStatus
  statusLabel: string
  employeeId: string
  medewerkerNaam: string
  entiteitNaam: string | null
  gestartOp: string
  einddatum: string | null
  /** De eerstvolgende termijn (of null). */
  volgendeTermijn: HrBesluitTermijnDTO | null
  openStappen: number
  totaalStappen: number
  /** De eerstvolgende open stap (titel) of null. */
  volgendeStap: string | null
  controleRol: HrBesluitRol
  wachtOpControle: boolean
}

export interface HrBesluitTellersDTO {
  lopend: number
  terControle: number
  concept: number
  termijnVerlopen: number
  termijnBinnen7Dagen: number
  perType: Record<HrBesluitType, number>
}

/** De keten van besluiten van één medewerker (dossier). */
export interface HrBesluitDossierDTO {
  employeeId: string
  medewerkerNaam: string
  workflows: HrBesluitLijstRijDTO[]
}

export interface OpzegtermijnRegelDTO {
  id: string
  bron: OpzegtermijnBron
  partij: OpzegtermijnPartij
  minDienstjaren: number
  maxDienstjaren: number | null
  termijnMaanden: number
  omschrijving: string | null
  actief: boolean
}

export interface VsoBibliotheekPuntDTO {
  id: string
  kop: string
  tekst: string
  volgorde: number
  actief: boolean
}

export interface OpzegtermijnBerekeningDTO {
  dienstjaren: number
  partij: OpzegtermijnPartij
  termijnMaanden: number
  bron: OpzegtermijnBron | null
  regelOmschrijving: string | null
  /** De vroegst mogelijke einddatum (tegen het einde van de maand), 'yyyy-mm-dd'. */
  vroegsteEinddatum: string
  /** De einddatum die geldt: de gewenste als die later is, anders de vroegste. */
  einddatum: string
  /** True als geen regel paste en de wettelijke terugval van één maand is gebruikt. */
  terugval: boolean
}

/** Contract-keuzes voor de wizard (aflopende contracten van de medewerker). */
export interface HrBesluitContractKeuzeDTO {
  id: string
  typeLabel: string
  startDatum: string
  eindDatum: string | null
  status: string
  aanzegplichtig: boolean
  /** Bestaand verlengingsbesluit (FO-388), of null. */
  besluit: string | null
}

export interface HrBesluitWizardContextDTO {
  employeeId: string
  medewerkerNaam: string
  entityId: string | null
  contracten: HrBesluitContractKeuzeDTO[]
  /** Lopend verzuim (voor de non-actief-bevestiging). */
  verzuimLoopt: boolean
  /** Beschikbare sjablonen per categorie (naam), zodat de wizard een ontbrekend sjabloon meldt. */
  sjablonen: Partial<Record<DocumentTemplateCategory, { id: string; naam: string }[]>>
  vsoBibliotheek: VsoBibliotheekPuntDTO[]
  /** Datum in dienst ('yyyy-mm-dd') voor de opzegtermijn-berekening. */
  startDate: string | null
  /** Recente documenten uit het dossier (voor "het document of de mail van de opzegging"). */
  documenten: { id: string; titel: string; datum: string }[]
}

/** Notificatie-keys van deze workflow (achteraan in de key-lijst). */
export const HR_BESLUIT_EVENT_KEYS = {
  CONTROLE_GEVRAAGD: 'HR_BESLUIT_CONTROLE_GEVRAAGD',
  GECONTROLEERD: 'HR_BESLUIT_GECONTROLEERD',
  STAP_OPEN: 'HR_BESLUIT_STAP_OPEN',
  TERMIJN_NADERT: 'HR_BESLUIT_TERMIJN_NADERT',
  TERMIJN_VERSTREKEN: 'HR_BESLUIT_TERMIJN_VERSTREKEN',
  BRIEF_VERSTREKT: 'HR_BESLUIT_BRIEF_VERSTREKT',
  VSO_HERROEPEN: 'HR_BESLUIT_VSO_HERROEPEN',
} as const

/** Bron-module op taken, postvak-items en meldingen van deze workflow. */
export const HR_BESLUIT_BRON_MODULE = 'hr-besluiten'

// ─── Mail-uitbox ────────────────────────────────────────────────────────────

/**
 * `OutgoingEmail.triggerEntityType` van een mail die bij een HR-besluit hoort
 * (het bericht aan de salarisverwerker). De mail-uitbox schermt de inhoud van
 * zo'n mail af voor wie het besluit niet mag zien.
 */
export const HR_BESLUIT_MAIL_ENTITY = 'HrBesluitWorkflow'

/** Wat de uitbox toont in plaats van de inhoud. */
export const HR_BESLUIT_MAIL_AFGESCHERMD =
  'Dit bericht hoort bij een HR-besluit. De inhoud is alleen in te zien voor wie het besluit mag zien (HRM > Besluiten en brieven).'
